import React from 'react';
import styled from '@emotion/styled';
import { ActivityEntry } from '../../api/activity-client';
import {
  layout,
  palette,
  radius,
  spacing,
  status,
  typography,
} from '../../style-utils/theme';
import { down, touch } from '../../style-utils/media-queries';
import { listItemMotion, m } from '../../motion';
import { Text, font } from '../../style-utils/text';
import { normalizeUserRole, roleLabel } from '../../auth/roles';
import { openRecordLabel, sourceNoun } from '../../function-utils/record-label';
import {
  clockTime,
  dayKeyIn,
  formatIn,
  todayIn,
} from '../../function-utils/building-time';

/* ═══════════ ONE ROW, TWO SURFACES ═══════════
 *
 * The activity feed's row, lifted out of <ActivityFeed> so the shift handover's
 * full log renders the SAME row (owner instruction, 2026-09-11: a handover
 * without Lobbero IQ is "a list of things that happened ... every log in
 * detail"). Two copies of this JSX is how the desk feed and the handover would
 * start disagreeing about what a row shows — the reason it was one renderer
 * inside the feed in the first place.
 *
 * `detailed` is the only difference the handover asks for: EVERY note on its own
 * line, instead of the first note run into the "who" line. A feed is a glance; a
 * handover is read.
 */

export const FilterToggle = styled.button`
  display: inline-flex;
  align-items: center;
  /* IT MUST NOT BE SQUEEZED INTO ITS OWN LABEL.
     This sits in a flex row beside the search field, with nothing stopping the
     row from shrinking it: measured at 390px on a phone it came out 39px wide
     for a 7-character word, so "Filters" wrapped and rendered as "Filter" over
     "s". The height was already right (44px via the touch rule) — it was only
     ever the width. NO BACKTICKS IN THIS COMMENT: styled template literal. */
  flex: 0 0 auto;
  white-space: nowrap;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  /* lagoon, not iris: this is 13px text, so it is measured at 4.5:1 and
     palette.iris is 3.20:1 on white. */
  color: ${palette.lagoon};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${palette.iris};
    outline-offset: 2px;
  }

  /* THE ROW'S OWN ACTION IS THE SMALLEST TARGET ON THE PANEL.
     With no padding and no min-height this control's hit area was exactly its
     13px text box — on the per-row Open button, on a feed the desk drives from
     a tablet, while every sibling control on the same panel already meets the
     floor. NO BACKTICKS IN THIS COMMENT: styled template literal. */
  /* AND WIDE ENOUGH, not only tall enough: "Open" is four letters, so text plus
     the half-step padding measured 41x44 on /shift-monitor and the menu's
     embedded feed — the width, never the height, fell short of the floor.
     Centred so the word stays put inside the wider box. */
  ${touch} {
    min-height: ${layout.touchTarget};
    min-width: ${layout.touchTarget};
    justify-content: center;
    padding: 0 ${spacing.half};
  }
`;

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

/* A motion element: rows arrive with the capped list cascade (see
   ActivityEntryRow). No layout animation — the list only ever grows, never
   reorders. */
const Row = m.li`
  display: grid;
  grid-template-columns: 64px 1fr auto;
  gap: 8px;
  align-items: baseline;
  padding: 8px 4px;
  border-top: 1px solid ${palette.line};

  &:first-of-type {
    border-top: none;
  }

  /* On a phone the third column has nowhere to go: "who did it" plus an Open
     action next to a wrapping sentence produces a two-character-wide middle
     column. Drop to two columns and let the actor sit under the entry, which is
     also the reading order. */
  ${down('sm')} {
    grid-template-columns: 52px 1fr;
    row-gap: 4px;

    & > :last-child {
      grid-column: 2;
    }
  }
`;

/* THE TIME CELL — a time, and a date UNDER it when the row is not from today.
 *
 * The two ran straight into each other ("09:26 AMAug 12") because both were
 * inline in one span: no separator, and nothing telling the eye that a second
 * value had started. They are two facts about one instant and stack as two
 * lines, which is also what keeps the column narrow.
 */
const When = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
  font-variant-numeric: tabular-nums;
  line-height: ${typography.leading.snug};

  /* The date. Quieter than the time — the time is what a desk reads first, and
     the date only qualifies it. */
  > b {
    font-weight: ${typography.weight.regular};
    opacity: 0.85;
    white-space: nowrap;
  }
`;

/* THE ROW'S TAGS.
 *
 * With one timeline instead of four grouped lists, these are what make a row
 * recognisable without reading it — so there are more tones than there were, and
 * each means one thing:
 *
 *   in / out   the direction, and the default look
 *   kind       the KIND inside the group (Guest, Delivery, Maintenance), which
 *              the group label alone does not say. Deliberately the quietest
 *              tone: it is a refinement, not a warning.
 *   open       still inside, or a building activity still running
 *   alert      an incident. The only tone that should stop a reader.
 *
 * Every colour is a token; status tints come in {bg, fg} pairs so the label
 * always matches its fill (design_system §2.3).
 */
/* THE VALIDATED TRIPLES, NOT A LOCAL RE-INVENTION.
 *
 * This table used to pair each semantic hue with ITSELF on its own soft tint —
 * palette.warning on palette.warningSoft is 1.92:1, palette.alert on
 * palette.alertSoft is 2.1:1 — so the two states a reader most needs to spot
 * were the two hardest to read. theme.ts already carries { bg, fg, dot } triples
 * whose fg is chosen to pass on its own bg (4.56:1 to 9.20:1), which is exactly
 * what this needed and what design_system.md §2.3 says to use.
 *
 * The `dot` slot is deliberately unused here: these are text pills, and the dot
 * is the colour for the marker beside a label, not for the label. */
const CHIP_TONE = {
  in: { bg: status.brand.bg, fg: status.brand.fg },
  out: { bg: status.brand.bg, fg: status.brand.fg },
  kind: { bg: status.neutral.bg, fg: status.neutral.fg },
  open: { bg: status.warning.bg, fg: status.warning.fg },
  alert: { bg: status.alert.bg, fg: status.alert.fg },
} as const;

export const Chip = styled.span<{ tone?: keyof typeof CHIP_TONE }>`
  display: inline-block;
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  padding: 0 ${spacing.half};
  border-radius: ${radius.xs};
  margin-right: ${spacing.half};
  white-space: nowrap;
  color: ${({ tone }) => CHIP_TONE[tone || 'in'].fg};
  background: ${({ tone }) => CHIP_TONE[tone || 'in'].bg};
`;

/* The row's second line: unit, carrier, counts, when it ended, who logged it.
 *
 * A SECOND LINE rather than more columns, because the useful facts differ per
 * source — a parcel has a carrier and a count, a visitor has a party size, an
 * activity has a priority — and a column per union member is a table of blanks.
 * Wraps and separates itself, so any subset reads correctly.
 */
const Meta = styled.span`
  display: block;
  margin-top: ${spacing.half};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};

  > span + span::before {
    content: ' · ';
  }
`;

/* EVERY NOTE, ONE PER LINE — the detailed row only. A handover is read, not
   glanced at, and the first note run into the who-line is how three of a
   visitor's notes became one. pre-line keeps the desk's own line breaks. */
const Notes = styled.span`
  display: block;
  margin-top: ${spacing.half};

  > span {
    display: block;
    font-size: ${typography.size.xs}px;
    color: ${palette.inkSoft};
    line-height: ${typography.leading.normal};
    white-space: pre-line;
  }
`;

// The window's own zone, so a desk day rendered here matches the one the server
// cut (Q-7/Q-9: a shift belongs to the building's clock, not the viewer's).
// An unusable zone does not take the feed down: the one time module falls back
// to the device's clock, and the screen's clock note says which clock it is.
export const timeIn = (iso: string, tz: string): string => clockTime(iso, tz);

/* THE DATE, for a feed that spans more than one day.
 *
 * The default window is SEVEN DAYS and every row showed a bare clock time, so
 * "07:24" could be any of them. Rendered only when the row is not from today —
 * which is most rows, most of the time, so the common case stays uncluttered
 * and the date earns its space exactly when it carries information.
 *
 * Both use the same zone as timeIn, so a row cannot be dated in one zone and
 * clocked in another.
 */
const dateIn = (iso: string, tz: string): string =>
  formatIn(iso, tz, { day: 'numeric', month: 'short' });

/* Same-calendar-day in the RENDERING zone, compared as day keys rather than by
 * arithmetic on instants — which is what keeps it correct across a zone whose
 * offset is not a whole number of hours. */
const isToday = (iso: string, tz: string): boolean => {
  const day = dayKeyIn(iso, tz);

  return Boolean(day) && day === todayIn(tz);
};

/**
 * WHO THE ROW IS ABOUT.
 *
 * For everything except a parcel handover that is simply the people on the
 * record. A PARCEL HANDOVER has two people with different jobs — the addressee
 * and whoever actually took it — and `people` is one de-duplicated array, so the
 * row read "Nils Sorensen, Gjorge Lazarov" and left the reader to guess which
 * was which. Worse, a resident collecting their own parcel de-duplicates to one
 * name and the row stopped mentioning the collection at all.
 *
 * Owner instruction, 2026-08-21: "picked up by has to be always visible
 * everywhere". The feed row is one of those everywheres.
 */
const whoOf = (entry: ActivityEntry): string => {
  if (entry.source === 'package' && entry.direction === 'out') {
    const forWhom = entry.parcelRecipient ? `For ${entry.parcelRecipient}` : '';
    // TWO WAYS OUT, and the row says which. A return had no collector by
    // definition, so "Picked up by someone not recorded" would read as a desk
    // failure on a parcel that was handled correctly.
    const by =
      entry.parcelEvent === 'return'
        ? 'Returned to the carrier'
        : // Said even when it is missing: "the desk released this and recorded
          // nobody" is the one state a supervisor has to see from the list.
          `Picked up by ${entry.parcelCollector || 'someone not recorded'}`;

    return [forWhom, by].filter(Boolean).join(' · ');
  }

  return entry.people.filter(Boolean).join(', ');
};

/** The feed's one-line summary: who, then the FIRST note. */
export const summarise = (entry: ActivityEntry): string => {
  const note = entry.text.find(Boolean) || '';

  return [whoOf(entry), note].filter(Boolean).join(' — ') || entry.kind;
};

/* WP-36 / MR-14 — THE ROW, IN THE BUTTON'S NAME.
 *
 * Every row in this feed ends in a button whose accessible name was the single
 * word "Open". Pulling up the list of controls — the standard way to move
 * through a page — produced a column of identical entries with no way to tell
 * which record any of them led to.
 *
 * The visible word stays "Open"; the row is right there for anyone who can see
 * it, and widening the button would cost the layout on a feed that is already
 * dense. Only the name a screen reader reads changes.
 *
 * BUILT FROM WHAT THE ROW ALREADY SHOWS — `summarise` is the same call the
 * visible line uses, so the two cannot drift, and `timeIn` is the same clock in
 * the same zone. Nothing here is disclosed that the row does not already print.
 */
const openEntryLabel = (entry: ActivityEntry, tz: string): string =>
  openRecordLabel(sourceNoun(entry.source), [
    summarise(entry),
    entry.unit ? `unit ${entry.unit}` : '',
    entry.at ? timeIn(entry.at, tz) : '',
  ]);

export interface ActivityEntryRowProps {
  entry: ActivityEntry;
  /** The zone every time on the row is rendered in — the BUILDING's. */
  tz: string;
  /** Position within its page, for the capped arrival cascade. */
  index: number;
  pageSize: number;
  /** Opens the record behind the row. Omitted → the row offers no Open control,
   *  rather than one that goes nowhere. */
  onOpen?: (entry: ActivityEntry) => void;
  /** Every note on its own line, instead of the first note run into the
   *  who-line. The handover's full log; the feed stays a glance. */
  detailed?: boolean;
  /** A surface's own tags, after the row's — "Changed since the handover". */
  extraChips?: React.ReactNode;
}

// ── A TIMELINE, NOT A GROUPED LIST ──
// Owner decision, 2026-08-13: everything in one stream, and the FILTERS are
// what produce a group. The grouped "All" view that shipped before made the
// reader assemble the actual sequence of a shift out of four separate lists —
// and the sequence is the thing a desk reads a feed for ("the parcel came in,
// then the guest arrived"). Every row carries a TAG, so one stream is still
// readable at a glance.
export const ActivityEntryRow: React.FC<ActivityEntryRowProps> = ({
  entry,
  tz,
  index,
  pageSize,
  onOpen,
  detailed = false,
  extraChips,
}) => {
  // The parcel count rides with the carrier rather than costing a column that
  // would read "1" on almost every row — the same rule the IQ parcel table
  // follows.
  const detail = [
    entry.subKind,
    entry.parcelCount && entry.parcelCount > 1 ? `${entry.parcelCount} items` : '',
    Number.isInteger(entry.headcount) && (entry.headcount as number) > 1
      ? `party of ${entry.headcount}`
      : '',
    entry.priority && entry.priority !== 'Low' ? entry.priority : '',
  ]
    .filter(Boolean)
    .join(' · ');

  // ---- ONE TAG PER ROW, NOT TWO SAYING THE SAME THING ----
  //
  // The tag is "<group> <direction>" — "Parcels out". The KIND is a second,
  // finer classifier and is worth a chip only when it ADDS something: "Guest"
  // and "Delivery" are both Visitors and the desk treats them differently.
  // A parcel's kind is literally "Parcel out", so rendering both produced
  // "Parcels out  Parcel out" — the same fact twice, which reads as a bug
  // because it is one.
  //
  // Compared on the WORDS rather than on a source allow-list, so a kind that
  // restates its group is dropped wherever it appears, including the next
  // source somebody adds.
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z]/g, '');
  const tag = `${entry.groupLabel}${entry.direction ? ` ${entry.direction}` : ''}`;
  const kindAddsSomething =
    !!entry.kind &&
    norm(entry.kind) !== norm(tag) &&
    norm(entry.kind) !== norm(entry.groupLabel) &&
    // Singular/plural of the same word: "Parcel out" vs "Parcels out".
    !norm(tag).startsWith(norm(entry.kind).replace(/s$/, '')) &&
    !norm(entry.kind).startsWith(norm(tag).replace(/s$/, ''));

  // ---- AN AUTO-CLOSE IS NOT AN OBSERVATION ----
  //
  // "out 01:59" on a 09:24 arrival is the nightly sweep tidying up a record
  // nobody closed, and rendering it as a plain departure time claims somebody
  // watched that person leave at two in the morning. chk_vl_exit_machine (059)
  // makes exit_source sufficient on its own to tell the two apart, so the row
  // says which it is — the same distinction the handover gives a section of
  // its own to.
  const observedExit = entry.exitSource === 'desk';

  const notes = detailed ? entry.text.filter(Boolean) : [];
  const lead = detailed ? whoOf(entry) || entry.kind : summarise(entry);

  return (
    <Row
      /* The cascade is per PAGE, not per absolute index: rows already on
         screen keep their keys and do not replay, and a page appended by
         "Show more" arrives with its own capped cascade from 0 rather than
         waiting out delays for rows that are already there. layout is off —
         this list only grows, it never reorders or removes. */
      {...listItemMotion(index % pageSize)}
      layout={false}
      data-at={entry.at}
      data-building-zone={entry.tz || undefined}
    >
      <When>
        <span data-time="">{timeIn(entry.at, tz)}</span>
        {/* THE DATE, only when the row is not from today. A feed spanning a
            week showed bare clock times, so "07:24" could be any of seven
            days — and the default window IS seven days. On its own line, so
            it reads as a date under a time rather than running into it. */}
        {!isToday(entry.at, tz) && <b>{dateIn(entry.at, tz)}</b>}
      </When>
      <span>
        {/* THE TAG. What kind of thing this is, and which way it went. With
            one stream instead of four lists, this is what makes a row
            recognisable without reading it. */}
        <Chip tone={entry.direction === 'out' ? 'out' : 'in'}>{tag}</Chip>
        {kindAddsSomething && <Chip tone="kind">{entry.kind}</Chip>}
        {entry.severity === 'incident' && <Chip tone="alert">Incident</Chip>}
        {entry.severity === 'follow_up' && <Chip tone="open">Follow up</Chip>}
        {/* THE SAME STATE, NAMED PER SUBJECT. A parcel is open too now (the
            server counts one still on the shelf in "Still inside"), and
            "Still inside" is the wrong words for it — a parcel is not a
            person in the lobby. */}
        {entry.open === true && (
          <Chip tone="open">
            {entry.source === 'daily_log'
              ? 'Still open'
              : entry.source === 'package'
                ? 'Not collected'
                : 'Still inside'}
          </Chip>
        )}
        {extraChips}
        <Text
          as="span"
          mt={0}
          mb={0}
          fontSize={13}
          fontWeight={500}
          fontFamily={font.family}
        >
          {lead}
        </Text>
        {notes.length ? (
          <Notes>
            {notes.map((note, k) => (
              <span key={k}>{note}</span>
            ))}
          </Notes>
        ) : null}
        <Meta>
          {entry.unit && <span>Unit {entry.unit}</span>}
          {detail && <span>{detail}</span>}
          {/* WHEN IT ENDED, and whether anybody saw it end.
              On an ARRIVAL only — a departure now has a row of its own at its
              own time, and the server sends departedAt null on that row so
              one instant is not printed twice (activity/feed.js). This still
              appears on an arrival whose departure fell outside the window
              and so has no row here. */}
          {entry.departedAt && (
            <span>
              {observedExit
                ? `out ${timeIn(entry.departedAt, tz)}`
                : 'closed automatically, nobody recorded them leaving'}
            </span>
          )}
          {/* THE OUT ROW'S OWN LINE. Its clock is already the departure, so
              what is left to say is the part a clock cannot: whether this was
              somebody at the desk watching, or the nightly sweep tidying up a
              record nobody closed. A parcel handover is excluded — it is
              always a person handing a parcel over, and exit_source is not a
              column on that table. */}
          {entry.direction === 'out' && entry.source !== 'package' && (
            <span>
              {observedExit
                ? 'recorded at the desk'
                : 'closed automatically, nobody recorded the end'}
            </span>
          )}
          {/* WHO DID IT, ALWAYS. A name we cannot decrypt renders as
              'Unknown', never as nothing and never as the raw token: a row
              with no author reads as a row nobody is accountable for, which
              is the opposite of what an unreadable name means.

              THE ROLE IS A LABEL, NOT THE STORED SLUG. It rendered as
              "(property_manager)" — a database value on a screen, which is
              the same class of leak as a raw id. roleLabel is the one place
              that mapping lives. */}
          <span>
            by {entry.actor?.name || 'Unknown'}
            {entry.actor?.name && normalizeUserRole(entry.actor.role)
              ? ` (${roleLabel(entry.actor.role, { short: true })})`
              : ''}
          </span>
        </Meta>
      </span>
      {onOpen ? (
        <span>
          {/* Every row points at a real record. The ref carries
              {source, id} — never the serialized 'v:24' token. */}
          <FilterToggle
            type="button"
            aria-label={openEntryLabel(entry, tz)}
            onClick={() => onOpen(entry)}
          >
            Open
          </FilterToggle>
        </span>
      ) : null}
    </Row>
  );
};
